import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signUp, login, requestPasswordReset, devSignIn } = useAuth();

  const DEV_ENABLED =
    window.location.hostname === 'localhost' ||
    process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [showReset, setShowReset] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signUp({ name, email, password });
      } else {
        await login( email, password );
      }
      navigate('/');
    } catch (err) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setError('');
    setResetMsg('');
    try {
      await requestPasswordReset(email);
      setResetMsg(`Reset link sent to ${email}. Check your inbox.`);
      setShowReset(false);
    } catch (err) {
      setError(err?.message || 'Could not send reset link');
    }
  };

  return (
    <div className="p-6 max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-4">{mode === 'signup' ? 'Create Account' : 'Sign In'}</h2>

      <div className="mb-4 flex gap-2">
        <button
          className={`px-3 py-2 rounded ${mode==='login' ? 'bg-blue-600 text-white' : 'border'}`}
          onClick={() => setMode('login')}
          type="button"
        >
          Sign In
        </button>
        <button
          className={`px-3 py-2 rounded ${mode==='signup' ? 'bg-blue-600 text-white' : 'border'}`}
          onClick={() => setMode('signup')}
          type="button"
        >
          Create Account
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {mode === 'signup' && (
          <input
            className="w-full p-3 border rounded"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        )}

        <input
          className="w-full p-3 border rounded"
          type="email"
          placeholder="Email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <input
          className="w-full p-3 border rounded"
          type="password"
          placeholder="Password"
          autoComplete={mode==='signup' ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {/* Forgot password */}
        {mode === 'login' && (
          <div className="text-sm">
            <button
              type="button"
              className="text-blue-600 underline"
              onClick={() => setShowReset((s) => !s)}
            >
              Forgot password?
            </button>
            {showReset && (
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-2 bg-gray-800 text-white rounded"
                >
                  Send Reset Link
                </button>
                <span className="text-xs text-gray-600">We’ll email a reset link to the address above.</span>
              </div>
            )}
          </div>
        )}

        {resetMsg && <div className="text-green-700 text-sm">{resetMsg}</div>}
        {error && <div className="text-red-600 text-sm">{error}</div>}

        <button
          disabled={busy}
          className="w-full bg-blue-600 text-white p-3 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? 'Please wait…' : (mode === 'signup' ? 'Create Account' : 'Sign In')}
        </button>
      </form>
    </div>
  );
}